/**
 * Session Launcher
 *
 * Step-by-step drawer for creating a session with a specific tool.
 * Driven entirely by the sessionLauncherEngine: it inherits whatever context
 * is already known and only asks about what isn't.
 *
 * Entry points:
 *  A. Tools page — "Start session with this tool" button per tool card
 *  B. Sessions page — "New Session" routed through here for tool-driven sessions
 *
 * Steps (some may be skipped if not needed):
 *  0. Context summary (auto) — what was inherited
 *  1. Modality (Qual / Quant) — skipped if tool has only one
 *  2. S-class — skipped if only one valid class exists
 *  3. Prerequisite gate — blocking prereqs shown with fix links; non-blocking warns
 *  4. Session config — name (always), optional fields
 *  5. Result destination — only when multiple options exist
 *  6. Confirmation — full summary before create
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  Rocket,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { buildLaunchPlan, getPrerequisiteRemediation } from '../../lib/sessionLauncherEngine';
import type { LaunchContext, LaunchQuestion, ResultDestination, ItemSource } from '../../lib/sessionLauncherEngine';
import { describeResultDestinations } from '../../lib/sessionResultRouter';
import type { DecisionClass, SessionIntent } from '../../types/prioritization/session';
import {
  DECISION_CLASS_LABELS,
  DECISION_CLASS_SHORT_LABELS,
  DECISION_CLASS_DESCRIPTIONS,
} from '../../types/prioritization/session';
import { getMethodForVariant, getVariantsForTool } from '../../lib/methodRegistry';
import type { SessionFit } from '../../lib/methodRegistry';
import { useUserStore } from '../../stores/prioritization/userStore';
import { createSessionWithFeedback } from '../../lib/sessions/createSessionFlow';
import { NEW_SESSION_DIALOG_TITLE, CREATE_SESSION_BUTTON_LABEL } from '../../lib/sessions/vocabulary';
import { useToast } from '../ui/Toast';
import { useToolItems } from '../../hooks/prioritization/useToolItems';

// ============================================================================
// Props
// ============================================================================

export interface SessionLauncherProps {
  /** Tool to launch */
  toolId: string;
  /** Human-readable tool name */
  toolName: string;
  /** Pre-selected variant (e.g. from tool selector recommendation) */
  variantId?: string;
  /** If launching from within a cycle context */
  cycleId?: string;
  /** If launching from within a track context */
  trackId?: string;
  /**
   * Pre-created prioritization context to link the new session to.
   * Passed when coming from ConfigurationSelector so the session is
   * immediately wired into condition resolution, stage visibility,
   * and the decision unit.
   */
  contextId?: string;
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Called when the drawer is closed without creating */
  onClose: () => void;
  /** Called after a session is successfully created, with the new session id */
  onCreated?: (sessionId: string) => void;
}

// ============================================================================
// Step state
// ============================================================================

type StepId = 'context' | 'modality' | 's_class' | 'item_source' | 'prereqs' | 'config' | 'destination' | 'confirm';

interface LauncherAnswers {
  modality?: 'Qualitative' | 'Quantitative';
  sClass?: DecisionClass;
  itemSource?: ItemSource;
  sessionName: string;
  resultDestination?: ResultDestination;
}

// ============================================================================
// Fit badge
// ============================================================================

function FitBadge({ fit }: { fit: SessionFit }) {
  if (fit === 'recommended') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] px-1.5 py-0.5 rounded">
        <CheckCircle className="h-3 w-3" />
        Recommended
      </span>
    );
  }
  if (fit === 'allowed_warn') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--mustard)] bg-[var(--mustard-dim)] px-1.5 py-0.5 rounded">
        <AlertTriangle className="h-3 w-3" />
        Usable
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--coral)] bg-[var(--coral-dim)] px-1.5 py-0.5 rounded">
      <XCircle className="h-3 w-3" />
      Not recommended
    </span>
  );
}

// ============================================================================
// Intent mapping
// ============================================================================

function sClassToIntent(sClass: DecisionClass): SessionIntent {
  const map: Record<DecisionClass, SessionIntent> = {
    S1_PARTICIPATION_PRIORITIZATION: 'select_attributes',
    S2_ITEM_POOL_SELECTION: 'select_item_pool',
    S3_ATTRIBUTE_SELECTION: 'select_attributes',
    S4_VALUES_CHOICE_SELECTION: 'select_attributes',
    S5_VALUE_SELECTION: 'converge_values',
    S6_ITEM_PRIORITIZATION: 'rank_priorities',
  };
  return map[sClass];
}

// ============================================================================
// Main component
// ============================================================================

export default function SessionLauncher({
  toolId,
  toolName,
  variantId,
  cycleId,
  trackId,
  contextId,
  isOpen,
  onClose,
  onCreated,
}: SessionLauncherProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const userId = useUserStore((s) => s.userId);


  // Get item count from workspace
  const { items: toolItems } = useToolItems();
  const workspaceItemCount = toolItems.length;

  // Launcher answers (filled across steps)
  const [answers, setAnswers] = useState<LauncherAnswers>({ sessionName: `${toolName} Session` });
  const [currentStep, setCurrentStep] = useState<StepId>('context');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Build launch context from what we know + answers so far
  const launchContext = useMemo((): LaunchContext => ({
    toolId,
    variantId: variantId ?? (answers.modality
      ? getVariantsForTool(toolId).find((v) => v.modality === answers.modality)?.variantId
      : undefined),
    cycleId,
    trackId,
    workspaceItemCount,
  }), [toolId, variantId, cycleId, trackId, workspaceItemCount, answers.modality]);

  // Build the launch plan
  const plan = useMemo(() => buildLaunchPlan(launchContext), [launchContext]);

  // Build the ordered step list based on questions required.
  // Context step only appears when there IS inherited context worth showing
  // (a linked cycle or track). Without it the step just says "Mode: Standalone"
  // and adds friction for no reason.
  const steps = useMemo((): StepId[] => {
    const s: StepId[] = [];

    if (cycleId || trackId) s.push('context');

    for (const q of plan.questionsRequired) {
      if (q.id === 'modality') s.push('modality');
      else if (q.id === 's_class') s.push('s_class');
      else if (q.id === 'item_source') s.push('item_source');
    }

    if (plan.prerequisitesBlocking.length > 0 || plan.prerequisitesWarning.length > 0) {
      s.push('prereqs');
    }

    s.push('config');

    const destQuestion = plan.questionsRequired.find((q) => q.id === 'result_destination');
    if (destQuestion) s.push('destination');

    s.push('confirm');
    return s;
  }, [plan, cycleId, trackId]);

  const currentStepIndex = steps.indexOf(currentStep);
  const isLastStep = currentStepIndex === steps.length - 1;
  const isFirstStep = currentStepIndex === 0;

  const canAdvance = useMemo(() => {
    switch (currentStep) {
      case 'context': return true;
      case 'modality': return !!answers.modality;
      case 's_class': return !!answers.sClass;
      case 'item_source': return !!answers.itemSource;
      case 'prereqs': return plan.prerequisitesBlocking.length === 0;
      case 'config': return answers.sessionName.trim().length > 0;
      case 'destination': return true;
      case 'confirm': return true;
    }
  }, [currentStep, answers, plan.prerequisitesBlocking]);

  const goNext = useCallback(() => {
    const next = steps[currentStepIndex + 1];
    if (next) setCurrentStep(next);
  }, [currentStepIndex, steps]);

  const goBack = useCallback(() => {
    const prev = steps[currentStepIndex - 1];
    if (prev) setCurrentStep(prev);
  }, [currentStepIndex, steps]);

  const handleCreate = useCallback(async () => {
    setCreating(true);
    setCreateError(null);

    const sClass = answers.sClass ?? plan.proposedSClass;
    const intent = sClassToIntent(sClass);
    // If the user explicitly chose manual entry, treat the session as isolated
    // even when workspace items exist — they opted out of importing.
    const mode = answers.itemSource === 'manual' ? 'isolated' : plan.sessionContextMode;

    // No mode of its own: createSession starts a session on a cycle or track
    // from the server's walk of the chain, the workflow included (S-40, 1039).
    const result = await createSessionWithFeedback({
      name: answers.sessionName.trim(),
      description: `${toolName} session — ${DECISION_CLASS_LABELS[sClass]}`,
      cycleId,
      trackId: trackId ?? null,
      sessionContextMode: mode,
      facilitatorId: userId,
      createdBy: userId,
      intent,
      // Pass selectedToolId directly so it's set on the session object before
      // SessionDetail mounts. Using setSelectedTool after creation runs a
      // compatibility check that fails for an empty session (no items yet).
      ...(toolId ? { selectedToolId: toolId } : {}),
      ...(contextId ? { contextId } : {}),
    }, { toast: showToast });

    if (!result.ok) {
      setCreateError(result.error ?? 'Session could not be created. Please try again.');
      setCreating(false);
      return;
    }

    if (onCreated) {
      onCreated(result.sessionId!);
    } else {
      navigate(`/prioritization/sessions/${result.sessionId}?tab=setup`);
    }
    onClose();
  }, [answers, plan, toolId, toolName, cycleId, trackId, contextId, userId, onCreated, onClose, navigate, showToast]);

  const handleClose = useCallback(() => {
    setAnswers({ sessionName: `${toolName} Session` });
    setCurrentStep('context');
    setCreateError(null);
    onClose();
  }, [toolName, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div className="relative ml-auto flex h-full w-full max-w-lg flex-col bg-[var(--bg-raised)] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--rule)] px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center bg-primary-100">
              <Rocket className="h-4 w-4 text-primary-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[var(--text-pri)]">{NEW_SESSION_DIALOG_TITLE}</h2>
              <p className="text-xs text-[var(--text-ter)]">{toolName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 text-[var(--text-ter)] hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 px-6 py-3 border-b border-[var(--rule)]">
          {steps.map((step, i) => (
            <div
              key={step}
              className={`h-1.5 rounded-full transition-all ${
                i < currentStepIndex
                  ? 'bg-primary-500 w-4'
                  : i === currentStepIndex
                    ? 'bg-primary-600 w-6'
                    : 'bg-[var(--hover-tint)] w-4'
              }`}
            />
          ))}
          <span className="ml-2 text-xs text-[var(--text-ter)]">
            Step {currentStepIndex + 1} of {steps.length}
          </span>
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Hard block: tool not in registry — surface before any steps */}
          {!plan.canLaunch && plan.blockedReason && !plan.prerequisitesBlocking.length && (
            <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4 text-sm text-[var(--coral)] mb-6">
              <div className="flex items-start gap-2">
                <XCircle className="h-4 w-4 text-[var(--coral)] mt-0.5 flex-shrink-0" />
                <div><strong>Cannot launch:</strong> {plan.blockedReason}</div>
              </div>
            </div>
          )}
          {currentStep === 'context' && (
            <ContextStep plan={plan} toolName={toolName} />
          )}
          {currentStep === 'modality' && (
            <ModalityStep
              toolId={toolId}
              selected={answers.modality}
              onSelect={(m) => setAnswers((a) => ({ ...a, modality: m }))}
            />
          )}
          {currentStep === 's_class' && (
            <SClassStep
              question={plan.questionsRequired.find((q) => q.id === 's_class')!}
              selected={answers.sClass ?? plan.proposedSClass}
              onSelect={(sc) => setAnswers((a) => ({ ...a, sClass: sc }))}
            />
          )}
          {currentStep === 'item_source' && (
            <ItemSourceStep
              question={plan.questionsRequired.find((q) => q.id === 'item_source')!}
              selected={answers.itemSource}
              onSelect={(s) => setAnswers((a) => ({ ...a, itemSource: s }))}
            />
          )}
          {currentStep === 'prereqs' && (
            <PrereqsStep plan={plan} launchContext={launchContext} />
          )}
          {currentStep === 'config' && (
            <ConfigStep
              name={answers.sessionName}
              onNameChange={(n) => setAnswers((a) => ({ ...a, sessionName: n }))}
            />
          )}
          {currentStep === 'destination' && (
            <DestinationStep
              plan={plan}
              selected={answers.resultDestination}
              sClass={answers.sClass ?? plan.proposedSClass}
              onSelect={(d) => setAnswers((a) => ({ ...a, resultDestination: d }))}
            />
          )}
          {currentStep === 'confirm' && (
            <ConfirmStep
              toolName={toolName}
              answers={answers}
              plan={plan}
              createError={createError}
            />
          )}
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between border-t border-[var(--rule)] px-6 py-4">
          <button
            onClick={isFirstStep ? handleClose : goBack}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
          >
            <ChevronLeft className="h-4 w-4" />
            {isFirstStep ? 'Cancel' : 'Back'}
          </button>

          {isLastStep ? (
            <button
              onClick={handleCreate}
              disabled={creating || !plan.canLaunch}
              className="flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Rocket className="h-4 w-4" />
              )}
              {creating ? 'Creating…' : CREATE_SESSION_BUTTON_LABEL}
            </button>
          ) : (
            <button
              onClick={goNext}
              disabled={!canAdvance}
              className="flex items-center gap-1.5 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Step sub-components
// ============================================================================

function ContextStep({ plan, toolName }: { plan: ReturnType<typeof buildLaunchPlan>; toolName: string }) {
  const ctx = plan.inheritedContext;
  const mode = plan.sessionContextMode;

  const modeLabels = {
    cycle_linked: 'Cycle-linked',
    app_linked: 'App-linked',
    isolated: 'Standalone',
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-[var(--text-pri)] mb-1">Session context</h3>
        <p className="text-sm text-[var(--text-ter)]">
          Here's what we know about the context for this session.
        </p>
      </div>

      <div className="border border-[var(--rule)] divide-y divide-[var(--rule)]">
        <ContextRow label="Tool" value={toolName} />
        <ContextRow label="Mode" value={modeLabels[mode]} />
        {ctx.cycleId && <ContextRow label="Cycle" value="Linked" />}
        {ctx.trackId && <ContextRow label="Track" value="Linked (pool inherited)" />}
        {ctx.itemPoolSource && (
          <ContextRow
            label="Item source"
            value={ctx.itemPoolSource === 'track_pool' ? 'Track item pool' : 'Workspace items'}
          />
        )}
      </div>

      {!plan.canLaunch && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4 text-sm text-[var(--coral)]">
          <div className="flex items-start gap-2">
            <XCircle className="h-4 w-4 text-[var(--coral)] mt-0.5 flex-shrink-0" />
            <div>
              <strong>Cannot launch:</strong> {plan.blockedReason}
            </div>
          </div>
        </div>
      )}

      {plan.canLaunch && plan.prerequisitesWarning.length > 0 && (
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-3 text-xs text-[var(--mustard)]">
          <div className="flex items-center gap-1.5 mb-1">
            <AlertTriangle className="h-3.5 w-3.5" />
            <strong>Warnings</strong>
          </div>
          <ul className="list-disc list-inside space-y-0.5">
            {plan.prerequisitesWarning.map((p) => (
              <li key={p}>{p.replace(/_/g, ' ').toLowerCase()}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ContextRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5">
      <span className="text-xs text-[var(--text-ter)]">{label}</span>
      <span className="text-xs font-medium text-[var(--text-pri)]">{value}</span>
    </div>
  );
}

function ModalityStep({
  toolId,
  selected,
  onSelect,
}: {
  toolId: string;
  selected?: 'Qualitative' | 'Quantitative';
  onSelect: (m: 'Qualitative' | 'Quantitative') => void;
}) {
  const variants = getVariantsForTool(toolId);
  const qualVariant = variants.find((v) => v.modality === 'Qualitative');
  const quantVariant = variants.find((v) => v.modality === 'Quantitative');

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-[var(--text-pri)] mb-1">Choose approach</h3>
        <p className="text-sm text-[var(--text-ter)]">
          This tool supports both qualitative and quantitative approaches.
        </p>
      </div>

      <div className="space-y-3">
        {qualVariant && (
          <OptionCard
            selected={selected === 'Qualitative'}
            onClick={() => onSelect('Qualitative')}
            title="Qualitative"
            description="Judgment-based — no numeric inputs required. Good for early-stage decisions or when data is scarce."
            badge={null}
          />
        )}
        {quantVariant && (
          <OptionCard
            selected={selected === 'Quantitative'}
            onClick={() => onSelect('Quantitative')}
            title="Quantitative"
            description="Score-driven — requires numeric attributes or ratings. Produces sortable, reproducible results."
            badge={null}
          />
        )}
      </div>
    </div>
  );
}

function SClassStep({
  question,
  selected,
  onSelect,
}: {
  question: LaunchQuestion & { id: 's_class' };
  selected: DecisionClass;
  onSelect: (sc: DecisionClass) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-[var(--text-pri)] mb-1">Decision class</h3>
        <p className="text-sm text-[var(--text-ter)]">
          What kind of decision will this session support?
        </p>
      </div>

      <div className="space-y-3">
        {question.options.map((sc) => {
          const fit = question.fitMap[sc];
          return (
            <OptionCard
              key={sc}
              selected={selected === sc}
              onClick={() => onSelect(sc)}
              title={DECISION_CLASS_LABELS[sc]}
              description={DECISION_CLASS_DESCRIPTIONS[sc]}
              badge={<FitBadge fit={fit} />}
            />
          );
        })}
      </div>
    </div>
  );
}

function ItemSourceStep({
  question,
  selected,
  onSelect,
}: {
  question: LaunchQuestion;
  selected?: ItemSource;
  onSelect: (s: ItemSource) => void;
}) {
  if (question.id !== 'item_source') return null;

  const labels: Record<ItemSource, string> = {
    workspace_items: 'Workspace items',
    track_pool: 'Track item pool',
    manual: 'Enter manually',
  };
  const descriptions: Record<ItemSource, string> = {
    workspace_items: 'Import items already in your workspace.',
    track_pool: 'Use the item pool from the linked track.',
    manual: 'Type or paste items directly into the tool — no workspace needed.',
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-[var(--text-pri)] mb-1">Item source</h3>
        <p className="text-sm text-[var(--text-ter)]">
          Where will the items for this session come from?
        </p>
      </div>
      <div className="space-y-3">
        {question.options.map((source) => (
          <OptionCard
            key={source}
            selected={selected === source}
            onClick={() => onSelect(source as ItemSource)}
            title={labels[source as ItemSource]}
            description={descriptions[source as ItemSource]}
            badge={null}
          />
        ))}
      </div>
    </div>
  );
}

function PrereqsStep({
  plan,
  launchContext,
}: {
  plan: ReturnType<typeof buildLaunchPlan>;
  launchContext: LaunchContext;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-[var(--text-pri)] mb-1">Prerequisites</h3>
        <p className="text-sm text-[var(--text-ter)]">
          Some conditions must be met before this session can run.
        </p>
      </div>

      {plan.prerequisitesBlocking.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-[var(--coral)] uppercase tracking-wide">Blocking</p>
          {plan.prerequisitesBlocking.map((prereq) => {
            const info = getPrerequisiteRemediation(prereq, launchContext);
            return (
              <div
                key={prereq}
                className="border border-[var(--coral)] bg-[var(--coral-dim)] p-3 flex items-start gap-3"
              >
                <XCircle className="h-4 w-4 text-[var(--coral)] mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--coral)]">{info.label}</p>
                  <p className="text-xs text-[var(--coral)] mt-0.5">{info.description}</p>
                  {info.remedyPath && (
                    <a
                      href={info.remedyPath}
                      className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-[var(--coral)] hover:text-[var(--coral)]"
                    >
                      Go fix this
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {plan.prerequisitesWarning.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-[var(--mustard)] uppercase tracking-wide">Warnings</p>
          {plan.prerequisitesWarning.map((prereq) => {
            const info = getPrerequisiteRemediation(prereq, launchContext);
            return (
              <div
                key={prereq}
                className="border border-[var(--mustard)] bg-[var(--mustard-dim)] p-3 flex items-start gap-3"
              >
                <AlertTriangle className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--mustard)]">{info.label}</p>
                  <p className="text-xs text-[var(--mustard)] mt-0.5">{info.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {plan.prerequisitesMet.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)] uppercase tracking-wide">Met</p>
          {plan.prerequisitesMet.map((prereq) => (
            <div
              key={prereq}
              className="border border-[var(--green)] bg-[var(--green-dim)] p-3 flex items-center gap-3"
            >
              <CheckCircle className="h-4 w-4 text-[var(--green)] flex-shrink-0" />
              <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                {prereq.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ConfigStep({
  name,
  onNameChange,
}: {
  name: string;
  onNameChange: (n: string) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-[var(--text-pri)] mb-1">Session details</h3>
        <p className="text-sm text-[var(--text-ter)]">Give this session a clear name.</p>
      </div>

      <div>
        <label htmlFor="session-name" className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
          Session name <span className="text-[var(--coral)]">*</span>
        </label>
        <input
          id="session-name"
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="e.g. Q2 Feature Prioritization"
          className="w-full border border-[var(--rule)] px-3 py-2.5 text-sm text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          autoFocus
        />
      </div>
    </div>
  );
}

function DestinationStep({
  plan,
  selected,
  sClass: _sClass,
  onSelect,
}: {
  plan: ReturnType<typeof buildLaunchPlan>;
  selected?: ResultDestination;
  sClass: DecisionClass;
  onSelect: (d: ResultDestination) => void;
}) {
  const destinationLabels: Record<ResultDestination, string> = {
    session_results: 'Session results',
    item_pool_update: 'Item pool update',
    criteria_artifact: 'Criteria set artifact',
    narrative_artifact: 'Narrative record',
  };
  const destinationDescriptions: Record<ResultDestination, string> = {
    session_results: 'Results are stored as scored/ranked session outputs.',
    item_pool_update: 'Results update the item pool membership for the next session.',
    criteria_artifact: 'Results are saved as a locked criteria set.',
    narrative_artifact: 'Results are saved as a narrative record (no scoring).',
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-[var(--text-pri)] mb-1">Result destination</h3>
        <p className="text-sm text-[var(--text-ter)]">
          Where should the results of this session be stored?
        </p>
      </div>

      <div className="space-y-3">
        {plan.resultDestinations.map((dest) => (
          <OptionCard
            key={dest}
            selected={selected === dest}
            onClick={() => onSelect(dest)}
            title={destinationLabels[dest]}
            description={destinationDescriptions[dest]}
            badge={null}
          />
        ))}
      </div>
    </div>
  );
}

function ConfirmStep({
  toolName,
  answers,
  plan,
  createError,
}: {
  toolName: string;
  answers: LauncherAnswers;
  plan: ReturnType<typeof buildLaunchPlan>;
  createError: string | null;
}) {
  const sClass = answers.sClass ?? plan.proposedSClass;
  const methodDef = plan.selectedVariant
    ? getMethodForVariant(plan.selectedVariant.variantId)
    : null;

  const effectiveMode = answers.itemSource === 'manual' ? 'isolated' : plan.sessionContextMode;

  const modeLabels = {
    cycle_linked: 'Cycle-linked',
    app_linked: 'App-linked',
    isolated: 'Standalone',
  };

  const itemSourceLabels: Record<string, string> = {
    workspace_items: 'Workspace items',
    track_pool: 'Track item pool',
    manual: 'Enter manually',
  };

  const ctx = plan.inheritedContext;

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-[var(--text-pri)] mb-1">Confirm session</h3>
        <p className="text-sm text-[var(--text-ter)]">Review the details before creating.</p>
      </div>

      <div className="border border-[var(--rule)] divide-y divide-[var(--rule)]">
        <SummaryRow label="Name" value={answers.sessionName} />
        <SummaryRow label="Tool" value={toolName} />
        {methodDef && (
          <SummaryRow
            label="Method"
            value={`${methodDef.name}${answers.modality ? ` (${answers.modality})` : ''}`}
          />
        )}
        <SummaryRow label="Decision class" value={DECISION_CLASS_SHORT_LABELS[sClass]} />
        <SummaryRow label="Mode" value={modeLabels[effectiveMode]} />
        {answers.itemSource && (
          <SummaryRow label="Items" value={itemSourceLabels[answers.itemSource] ?? answers.itemSource} />
        )}
        {ctx.cycleId && <SummaryRow label="Cycle" value="Linked" />}
        {ctx.trackId && <SummaryRow label="Track" value="Linked (pool inherited)" />}
        <SummaryRow
          label="Results go to"
          value={describeResultDestinations(plan.outputForms, sClass)}
        />
      </div>

      {plan.prerequisitesWarning.length > 0 && (
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-3 flex items-start gap-2 text-sm text-[var(--mustard)]">
          <Info className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
          <span>
            This session has {plan.prerequisitesWarning.length} warning
            {plan.prerequisitesWarning.length > 1 ? 's' : ''} — results may be incomplete.
          </span>
        </div>
      )}

      {createError && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-3 text-sm text-[var(--coral)]">
          {createError}
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between px-4 py-3 gap-4">
      <span className="text-xs text-[var(--text-ter)] flex-shrink-0">{label}</span>
      <span className="text-xs font-medium text-[var(--text-pri)] text-right">{value}</span>
    </div>
  );
}

// ============================================================================
// Shared option card
// ============================================================================

function OptionCard({
  selected,
  onClick,
  title,
  description,
  badge,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  description: string;
  badge: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left  border-2 px-4 py-3.5 transition-all ${
        selected
          ? 'border-primary-500 bg-primary-50'
          : 'border-[var(--rule)] hover:border-[var(--rule)] bg-[var(--bg-raised)]'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${selected ? 'text-primary-900' : 'text-[var(--text-pri)]'}`}>
            {title}
          </p>
          <p className={`text-xs mt-0.5 ${selected ? 'text-primary-700' : 'text-[var(--text-ter)]'}`}>
            {description}
          </p>
        </div>
        <div className="flex-shrink-0 mt-0.5 flex items-center gap-2">
          {badge}
          <div
            className={`h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              selected ? 'border-primary-500 bg-primary-500' : 'border-[var(--rule)]'
            }`}
          >
            {selected && <div className="h-1.5 w-1.5 rounded-full bg-[var(--bg-raised)]" />}
          </div>
        </div>
      </div>
    </button>
  );
}
